/**
 * Helper to find an element by ID in the top-level sections of the form
 */
const findElementById = (fullForm, elementId) => {
  if (!fullForm?.sections) return null;

  for (const section of fullForm.sections) {
    const element = section.elements?.find(el => el.element_id === elementId);
    if (element) return element;
  }
  return null;
};

/**
 * Recursively search for an element by ID across sections,
 * including elements nested inside additional_form of any element type.
 */
const findElementDeep = (sections, elementId) => {
  for (const section of sections || []) {
    for (const element of section.elements || []) {
      if (element.element_id === elementId) return element;
      if (element.additional_form?.sections) {
        const found = findElementDeep(element.additional_form.sections, elementId);
        if (found) return found;
      }
    }
  }
  return null;
};

/**
 * Compute the reduced cover depth result using the three input fields.
 * result = ((minimumElectronicDepthReadingFeet * 12) + minimumElectronicDepthReadingInches) - gasLineSize / 2
 * @param {Array} sections - array of form sections to search for input values
 * @returns {number|null} the computed result, or null if any input value is missing
 */
const computeReducedCoverResult = sections => {
  const feetEl = findElementDeep(sections, 'minimumElectronicDepthReadingFeet');
  const inchesEl = findElementDeep(sections, 'minimumElectronicDepthReadingInches');
  const sizeEl = findElementDeep(sections, 'gasLineSize');

  const feet = feetEl?.value;
  const inches = inchesEl?.value;
  const size = sizeEl?.value;

  if (
    feet === undefined ||
    feet === null ||
    inches === undefined ||
    inches === null ||
    size === undefined ||
    size === null
  ) {
    return null;
  }

  return feet * 12 + inches - size / 2;
};

/**
 * Checks if "highlighted" is selected in conductingMethods
 */
const isHighlightedSelected = fullForm => {
  const conductingMethods = findElementById(fullForm, 'conductingMethods');
  if (!conductingMethods) return false;

  const selectedIndices = conductingMethods.leftSelectedIndices || [];
  const options = conductingMethods.leftSelectionOptions || [];

  return selectedIndices.some(index => options[index]?.option_id === 'highlighted');
};

const isCheckboxChecked = (fullForm, checkboxId) => {
  const checkboxElement = findElementById(fullForm, checkboxId);
  return Boolean(checkboxElement?.is_checked);
};

const HIGHLIGHTED_HIDDEN_ELEMENT_IDS = new Set([
  'gasTransmissionLinesCount',
  'gasTransmissionLines',
  'reducedCoverAdditionalQuestions',
  'landUseLocation',
  'siteConcerns',
]);

const HIGHLIGHTED_HIDDEN_SECTION_IDS = new Set(['reducedCoverSection']);

const LOW_PRESSURE_REGULATOR_HIDDEN_ELEMENT_IDS = new Set([
  'excavationBelowPavementLayer',
  'lawnAndGardenExcavationLimitedTo12Inches',
  'stumpGrindingLimitedTo12Inches',
  'drivingStakesExcavationLimitedTo12Inches',
  'agriculturalWorkSeparation',
]);

const REDUCED_COVER_HIDDEN_ELEMENT_IDS = new Set([
  'reducedCoverAdditionalQuestions',
  'landUseLocation',
  'siteConcerns',
  'reducedCoverPhotos',
]);

/**
 * Checks if gasTransmission follow-ups should be hidden
 * (when gasTransmission is checked AND highlighted is selected)
 */
export const shouldHideFollowUps = (element, fullForm) => {
  return (
    element.element_id === 'gasTransmission' &&
    element.is_checked &&
    isHighlightedSelected(fullForm)
  );
};

/**
 * Checks if a gasTransmission follow-up element should be excluded
 */
export const shouldExcludeGasTransmissionFollowUp = (element, fullForm) => {
  const targetIds = ['gasTransmissionLinesCount', 'gasTransmissionLines'];
  if (!targetIds.includes(element.element_id)) return false;

  const gasTransmission = findElementById(fullForm, 'gasTransmission');
  return gasTransmission ? shouldHideFollowUps(gasTransmission, fullForm) : false;
};

export const shouldHideHighlightedElement = (element, fullForm) => {
  if (!element?.element_id) return false;

  return isHighlightedSelected(fullForm) && HIGHLIGHTED_HIDDEN_ELEMENT_IDS.has(element.element_id);
};

export const shouldHideLowPressureRegulatorElement = (element, fullForm) => {
  if (!element?.element_id) return false;

  return (
    isCheckboxChecked(fullForm, 'lowPressureRegulator') &&
    LOW_PRESSURE_REGULATOR_HIDDEN_ELEMENT_IDS.has(element.element_id)
  );
};

/**
 * Hides reduced-cover-related elements when the calculated depth result >= 36 inches.
 * Uses a deep search through sections (including nested additional_form of any element type).
 * When input values are missing the result is null — defaults to showing the fields.
 */
export const shouldHideReducedCoverElement = (element, fullForm) => {
  if (!element?.element_id) return false;
  if (!REDUCED_COVER_HIDDEN_ELEMENT_IDS.has(element.element_id)) return false;

  const result = computeReducedCoverResult(fullForm?.sections);
  return result !== null && result >= 36;
};

/**
 * Hides the reducedCoverSection inside a subform when the calculated depth result >= 36 inches.
 * @param {object} section - the section object (must have an `id` property)
 * @param {Array} subformSections - the sections array to source input field values from
 */
export const shouldHideReducedCoverSection = (section, subformSections) => {
  if (section?.id !== 'reducedCoverSection') return false;

  const result = computeReducedCoverResult(subformSections);
  return result !== null && result >= 36;
};

export const shouldHideConditionalElement = (element, fullForm) => {
  return (
    shouldHideHighlightedElement(element, fullForm) ||
    shouldHideLowPressureRegulatorElement(element, fullForm) ||
    shouldHideReducedCoverElement(element, fullForm)
  );
};

export const shouldHideHighlightedSection = (section, fullForm) => {
  if (!section?.id) return false;

  return isHighlightedSelected(fullForm) && HIGHLIGHTED_HIDDEN_SECTION_IDS.has(section.id);
};
